import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Bone, ShieldCheck, Droplet, Sparkles, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchProducts } from "@/lib/api";

const benefitIcons = [Brain, Bone, ShieldCheck, Droplet, Sparkles, Activity];

const ProductCard = ({ product, index }) => {
  const accent = product.color_theme;
  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="bg-white rounded-3xl border border-border overflow-hidden product-card-hover"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div
        className="relative aspect-[4/3] overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent}15, ${accent}05)`,
        }}
      >
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-6"
          loading="lazy"
        />
        <Badge
          className="absolute top-4 left-4 rounded-full font-medium border-0 text-white"
          style={{ backgroundColor: accent }}
        >
          {product.category}
        </Badge>
      </div>

      <div className="p-6 sm:p-7">
        <h3
          className="font-heading font-bold text-2xl text-foreground tracking-tight"
          data-testid={`product-name-${product.slug}`}
        >
          {product.name}
        </h3>
        <p className="mt-1 text-sm font-medium" style={{ color: accent }}>
          {product.tagline}
        </p>

        <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {product.short_description}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {product.benefits.slice(0, 3).map((b, i) => {
            const Icon = benefitIcons[i % benefitIcons.length];
            return (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-medium"
              >
                <Icon className="w-3.5 h-3.5" />
                {b}
              </span>
            );
          })}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div className="bg-secondary/50 rounded-xl p-3">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
              Pack
            </p>
            <p className="text-foreground font-medium mt-0.5">{product.packaging}</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-3">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
              Composition
            </p>
            <p className="text-foreground font-medium mt-0.5 line-clamp-1">
              {product.composition[0]?.name}
            </p>
          </div>
        </div>

        <Link
          to={`/catalogue?slide=${product.slug}`}
          data-testid={`view-brochure-${product.slug}`}
        >
          <Button
            className="mt-6 w-full rounded-full h-11 text-white"
            style={{ backgroundColor: accent }}
          >
            View Brochure
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    </article>
  );
};

export const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-20 sm:py-28 bg-background"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p className="text-sm font-semibold tracking-widest uppercase text-primary">
            The Pediarange Series
          </p>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground">
            Three pediatric essentials,
            <span className="gradient-text-blue"> one trusted family.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Engineered for growing bodies and developing minds. Each formulation in
            the Pediarange line is built around composition, palatability and
            packaging that pediatricians and parents can rely on.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-secondary/40 rounded-3xl animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
